import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost } from "../lib/api";
import { useDecidePlanRequest, PlanRequestSummary } from "../lib/queries";
import { ErrorState } from "./Loading";

/**
 * Where a family's plan request stands, said to whoever is reading (27 Sep debug notes item 12).
 *
 * The family sees a pending request they can take back, and -- until they dismiss it -- the coach's
 * answer, which is the whole of "the client is notified either way" while email is sandboxed. The coach
 * sees a pending request with the two buttons that answer it.
 */
export function PlanRequestStatus({
  athlete,
  actingAs = "family",
}: {
  athlete: { client_id: string; name: string; plan_request?: PlanRequestSummary | null };
  actingAs?: "family" | "coach";
}) {
  const queryClient = useQueryClient();
  const decide = useDecidePlanRequest();
  const familyAction = useMutation({
    mutationFn: (action: "withdraw" | "dismiss") =>
      apiPost(`/billing/athletes/${athlete.client_id}/plan-request/${action}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["billing"] }),
  });

  const request = athlete.plan_request;
  if (!request) return null;
  const planName = request.plan_name ?? "another plan";

  if (request.status === "pending") {
    return (
      <div className="confirm-box" role="status" aria-label={`${athlete.name}'s plan request`}>
        <p>
          {actingAs === "coach" ? (
            <>
              {athlete.name}&rsquo;s family asked to move to <strong>{planName}</strong>.
            </>
          ) : (
            <>
              You asked to move {athlete.name} to <strong>{planName}</strong>. Waiting for your coach to
              answer &mdash; nothing changes until they do.
            </>
          )}
        </p>
        {(decide.error ?? familyAction.error) && <ErrorState error={decide.error ?? familyAction.error} />}
        {actingAs === "coach" ? (
          <div className="detail-actions">
            <button
              type="button"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ clientId: athlete.client_id, outcome: "approve" })}
            >
              Approve
            </button>
            <button
              type="button"
              className="chip"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ clientId: athlete.client_id, outcome: "decline" })}
            >
              Decline
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="chip"
            disabled={familyAction.isPending}
            onClick={() => familyAction.mutate("withdraw")}
          >
            Withdraw request
          </button>
        )}
      </div>
    );
  }

  // An answer, for the family until they've read it. The coach made it, so they need no reminder.
  if (actingAs === "coach" || request.family_seen) return null;

  return (
    <div className="confirm-box" role="status" aria-label={`${athlete.name}'s plan request`}>
      <p>
        {request.status === "approved" ? (
          <>
            Your coach approved moving {athlete.name} to <strong>{planName}</strong>.
          </>
        ) : (
          <>
            Your coach didn&rsquo;t approve moving {athlete.name} to <strong>{planName}</strong>
            {request.reason ? <> &mdash; {request.reason.toLowerCase()}</> : null}. Nothing has changed.
          </>
        )}
      </p>
      {familyAction.error && <ErrorState error={familyAction.error} />}
      <button type="button" className="chip" disabled={familyAction.isPending} onClick={() => familyAction.mutate("dismiss")}>
        Dismiss
      </button>
    </div>
  );
}
